import { useLayoutEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  pickDefaultDepartment,
  pickDefaultFinancialYear
} from './financialYearHelpers';

const buildSearchParams = (departmentValue, financialYearValue) => {
  const params = new URLSearchParams();
  if (departmentValue) params.set('department', departmentValue);
  if (financialYearValue !== undefined && financialYearValue !== null) {
    params.set('financialYearId', String(financialYearValue));
  }
  return params;
};

export const useDepartmentSpendingSelection = ({
  departmentSelectOptions,
  financialYearSelectOptions,
  department,
  setDepartment,
  financialYear,
  setFinancialYear
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const didApplyInitialDefaultsRef = useRef(false);
  const isInternalNavigationRef = useRef(false);

  useLayoutEffect(() => {
    if (!departmentSelectOptions.length || !financialYearSelectOptions.length)
      return;

    if (isInternalNavigationRef.current) {
      isInternalNavigationRef.current = false;
      return;
    }

    const departmentParam = searchParams.get('department');
    const financialYearIdParam = searchParams.get('financialYearId');

    if (
      !departmentParam &&
      !financialYearIdParam &&
      !didApplyInitialDefaultsRef.current
    ) {
      didApplyInitialDefaultsRef.current = true;
      const defaultDepartment = pickDefaultDepartment(departmentSelectOptions);
      const defaultFinancialYear = pickDefaultFinancialYear(
        financialYearSelectOptions
      );
      setDepartment(defaultDepartment);
      setFinancialYear(defaultFinancialYear);
      isInternalNavigationRef.current = true;
      setSearchParams(
        buildSearchParams(
          defaultDepartment?.value,
          defaultFinancialYear?.value
        ),
        { replace: true }
      );
      return;
    }
    didApplyInitialDefaultsRef.current = true;

    const matchedDepartment = departmentParam
      ? departmentSelectOptions.find(
          option => option.value === departmentParam
        ) || null
      : null;
    const matchedFinancialYear = financialYearIdParam
      ? financialYearSelectOptions.find(
          option => String(option.value) === financialYearIdParam
        ) || null
      : null;

    setDepartment(prev =>
      (prev?.value ?? null) === (matchedDepartment?.value ?? null)
        ? prev
        : matchedDepartment
    );
    setFinancialYear(prev =>
      (prev?.value ?? null) === (matchedFinancialYear?.value ?? null)
        ? prev
        : matchedFinancialYear
    );
  }, [
    searchParams,
    departmentSelectOptions,
    financialYearSelectOptions,
    setDepartment,
    setFinancialYear,
    setSearchParams
  ]);

  const handleDepartmentChange = option => {
    setDepartment(option);
    isInternalNavigationRef.current = true;
    setSearchParams(buildSearchParams(option?.value, financialYear?.value), {
      replace: false
    });
  };

  const handleFinancialYearChange = option => {
    setFinancialYear(option);
    isInternalNavigationRef.current = true;
    setSearchParams(buildSearchParams(department?.value, option?.value), {
      replace: false
    });
  };

  return { handleDepartmentChange, handleFinancialYearChange };
};
